import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./OperatorMenu.css";

const API = process.env.REACT_APP_API_URL || "http://localhost:3001";

// Normalisation simple (évite les soucis d'accents / casse / espaces)
function norm(s) {
  return (s ?? "")
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export default function OperatorMenu() {
  const navigate = useNavigate();
  const { name } = useParams();

  const decodedName = decodeURIComponent(name || "");
  const isElissa = decodedName.trim().toUpperCase() === "ELISSA";

  const items = useMemo(
    () => [
      { key: "demandes-transmises", label: "Demandes transmises" },
      { key: "sans-affectation", label: "Sans Affectation" },
      { key: "eaci", label: "EACI" },
      { key: "demandes-orientees", label: "Demandes orientées" },
      { key: "demandes-annulees", label: "Demandes annulées" },
      { key: "demandes-transferees", label: "Demandes transférées" },
      isElissa
        ? { key: "flechage", label: "Fléchage" }
        : { key: "retour-decision-cds", label: "Retour décision CDS" },
      { key: "liste-attente", label: "Liste d'attente" },
    ],
    [isElissa]
  );

  const [counts, setCounts] = useState({
    "demandes-transmises": 0,
    "sans-affectation": 0,
    eaci: 0,
    "demandes-orientees": 0,
    "demandes-annulees": 0,
    "demandes-transferees": 0,
    "liste-attente": 0,
    flechage: 0,
    "retour-decision-cds": 0,
  });

  // ✅ prioritaire par item (badge rouge avec nombre)
  const [prioCounts, setPrioCounts] = useState({
    "demandes-transmises": 0,
    "sans-affectation": 0,
    eaci: 0,
    "demandes-orientees": 0,
    "demandes-annulees": 0,
    "demandes-transferees": 0,
    "liste-attente": 0,
    flechage: 0,
    "retour-decision-cds": 0,
  });

  // ✅ alerte par item (point d'exclamation)
  const [alertCounts, setAlertCounts] = useState({
    "demandes-transmises": 0,
    "sans-affectation": 0,
    eaci: 0,
    "demandes-orientees": 0,
    "demandes-annulees": 0,
    "demandes-transferees": 0,
    "liste-attente": 0,
    flechage: 0,
    "retour-decision-cds": 0,
  });

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  useEffect(() => {
    let mounted = true;

    async function loadCounts() {
      try {
        const res = await fetch(`${API}/api/demandes`);
        if (!res.ok) return;

        const data = await res.json();
        const all = Array.isArray(data) ? data : [];

        const op = norm(decodedName);
        const byOperateur = all.filter((d) => norm(d.operateur) === op);

        const transmisesList = byOperateur.filter((d) => {
          const st = norm(d.statut_demande);
          const isTransmise = st === "" || st === norm("Demandes transmises");
          return isTransmise && norm(d.flechage) === "";
        });

        const sansAffectationList = byOperateur.filter(
          (d) => norm(d.statut_demande) === norm("Sans Affectation")
        );

        const eaciList = byOperateur.filter((d) => norm(d.statut_demande) === norm("EACI"));

        const orienteesList = byOperateur.filter((d) => {
          const s = norm(d.statut_demande);
          return s === norm("Demandes orientées") || s === norm("Demandes orientees");
        });

        const annuleesList = byOperateur.filter((d) => {
          const s = norm(d.statut_demande);
          return s === norm("Demandes annulées") || s === norm("Demandes annulees");
        });

        const transfereesList = byOperateur.filter((d) => {
          const s = norm(d.statut_demande);
          return s === norm("Demandes transférées") || s === norm("Demandes transferees");
        });

        const listeAttenteList = byOperateur.filter((d) => {
          const s = norm(d.statut_demande);
          return s === norm("Liste d'attente") || s === norm("Liste d’attente");
        });

        const retourCdsList = byOperateur.filter((d) => {
          const s = norm(d.statut_demande);
          return (
            s === norm("Retour décision CDS") ||
            s === norm("Retour decision cds") ||
            s === norm("Retour decision CDS")
          );
        });

        // Fléchage = global (comme avant)
        const flechageList = all.filter(
          (d) =>
            norm(d.flechage) === norm("ELISSA") &&
            (norm(d.statut_demande) === norm("Fléchage") ||
              norm(d.statut_demande) === norm("Flechage"))
        );

        const nextCounts = {
          "demandes-transmises": transmisesList.length,
          "sans-affectation": sansAffectationList.length,
          eaci: eaciList.length,
          "demandes-orientees": orienteesList.length,
          "demandes-annulees": annuleesList.length,
          "demandes-transferees": transfereesList.length,
          "liste-attente": listeAttenteList.length,
          flechage: flechageList.length,
          "retour-decision-cds": retourCdsList.length,
        };

        const nextPrioCounts = {
          "demandes-transmises": transmisesList.filter((d) => !!d.prioritaire).length,
          "sans-affectation": sansAffectationList.filter((d) => !!d.prioritaire).length,
          eaci: eaciList.filter((d) => !!d.prioritaire).length,
          "demandes-orientees": orienteesList.filter((d) => !!d.prioritaire).length,
          "demandes-annulees": annuleesList.filter((d) => !!d.prioritaire).length,
          "demandes-transferees": transfereesList.filter((d) => !!d.prioritaire).length,
          "liste-attente": listeAttenteList.filter((d) => !!d.prioritaire).length,
          flechage: flechageList.filter((d) => !!d.prioritaire).length,
          "retour-decision-cds": retourCdsList.filter((d) => !!d.prioritaire).length,
        };

        const nextAlertCounts = {
          "demandes-transmises": transmisesList.filter((d) => !!d.alerte).length,
          "sans-affectation": sansAffectationList.filter((d) => !!d.alerte).length,
          eaci: eaciList.filter((d) => !!d.alerte).length,
          "demandes-orientees": orienteesList.filter((d) => !!d.alerte).length,
          "demandes-annulees": annuleesList.filter((d) => !!d.alerte).length,
          "demandes-transferees": transfereesList.filter((d) => !!d.alerte).length,
          "liste-attente": listeAttenteList.filter((d) => !!d.alerte).length,
          flechage: flechageList.filter((d) => !!d.alerte).length,
          "retour-decision-cds": retourCdsList.filter((d) => !!d.alerte).length,
        };

        if (!mounted) return;
        setCounts(nextCounts);
        setPrioCounts(nextPrioCounts);
        setAlertCounts(nextAlertCounts);
      } catch (e) {
        console.error(e);
      }
    }

    loadCounts();
    return () => {
      mounted = false;
    };
  }, [decodedName]);

  const handleExportExcel = async () => {
    setExportError("");
    setExporting(true);
    try {
      const res = await fetch(`${API}/api/export/excel`);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Erreur export");
      }

      const blob = await res.blob();

      // tentative de récupérer le filename depuis Content-Disposition
      const cd = res.headers.get("content-disposition") || "";
      let filename = "export_siao.xlsx";
      const match = cd.match(/filename="([^"]+)"/i);
      if (match && match[1]) filename = match[1];

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setExportError("Impossible d’exporter le fichier Excel.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="operator-menu">
      <div className="operator-title">{decodedName}</div>

      <div className="grid-menu">
        {items.map((it) => {
          const n = counts[it.key] ?? 0;
          const p = prioCounts[it.key] ?? 0;
          const a = alertCounts[it.key] ?? 0;

          return (
            <div
              key={it.key}
              className="menu-box"
              onClick={() => navigate(`/operateur/${encodeURIComponent(name)}/${it.key}`)}
              style={{ position: "relative" }}
            >
              {it.label}

              {/* ✅ badges regroupés (alerte + prioritaire + total) en haut à droite */}
              <div
                style={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {/* Alerte : point d'exclamation */}
                {a > 0 ? (
                  <span
                    title={`${a} dossier(s) en alerte`}
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 999,
                      background: "#dc2626",
                      color: "white",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 950,
                      lineHeight: "22px",
                      boxShadow: "0 10px 22px rgba(220,38,38,0.18)",
                    }}
                  >
                    !
                  </span>
                ) : null}

                {/* Prioritaire : badge rouge avec nombre */}
                {p > 0 ? (
                  <span
                    title={`${p} dossier(s) prioritaire(s)`}
                    style={{
                      minWidth: 22,
                      height: 22,
                      padding: "0 6px",
                      borderRadius: 999,
                      background: "#b91c1c",
                      color: "white",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 950,
                      lineHeight: "22px",
                      boxShadow: "0 10px 22px rgba(185,28,28,0.18)",
                    }}
                  >
                    {p}
                  </span>
                ) : null}

                {/* Total */}
                <span
                  style={{
                    minWidth: 22,
                    height: 22,
                    padding: "0 6px",
                    borderRadius: 999,
                    background: "#111827",
                    color: "white",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 950,
                    lineHeight: "22px",
                    boxShadow: "0 10px 22px rgba(2,6,23,0.10)",
                  }}
                >
                  {n}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ✅ Export uniquement pour ELISSA */}
      {isElissa ? (
        <>
          <button
            className="back-button"
            onClick={handleExportExcel}
            disabled={exporting}
            style={{ maxWidth: 420 }}
          >
            {exporting ? "Export en cours..." : "Exporter Excel (tout le logiciel)"}
          </button>

          {exportError ? (
            <div style={{ color: "red", fontWeight: 800, marginTop: 8 }}>
              {exportError}
            </div>
          ) : null}
        </>
      ) : null}

      <button className="back-button" onClick={() => navigate("/")}>
        Retour
      </button>
    </div>
  );
}
